import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { CoverDoc } from 'fym-shared/src/music24/coverDoc';
import { useGetReleaseKitQuery } from '../../../redux/api/fymAPI';
import { TypedMemo } from '../../common/types';
import { ConfirmDialog } from '../../common/views/modals/confirmDialog/ConfirmDialog';
import { CoverThumb } from '../canvas/CoverThumb';
import { coverEditorMessages, useAsCoverErrorMessages } from '../coverEditorMessages';
import type { UseAsCoverError } from './useUseAsCover';
import { withBrandWord } from '../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface UseAsCoverDialogProps {
  isOpen: boolean;
  accountId: number;
  catalogItemUuid: string;
  doc: CoverDoc;
  /** The cover that the song has now. */
  currentCoverUrl: string | null;
  songTitle: string;
  watermark: boolean;
  isBusy: boolean;
  error: UseAsCoverError | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const SWAP_PX = 150;

/**
 * The confirm of "Use as cover" for a song that has a cover (design cover-editor e6): the old
 * cover and the new one side by side, and how many Studio assets still show the old cover until
 * they are made again.
 */
const UseAsCoverDialogComponent: React.FC<UseAsCoverDialogProps> = (props) => {
  const { isOpen, accountId, catalogItemUuid, doc, currentCoverUrl, songTitle, watermark, isBusy, error } = props;
  const { onConfirm, onCancel } = props;
  const intl = useIntl();
  const kit = useGetReleaseKitQuery({ accountId, catalogItemUuid }, { skip: !isOpen });
  const assetCount = useMemo(
    () => (kit.data?.assets ?? []).filter((asset) => asset.status === 'ready').length,
    [kit.data]
  );
  return (
    <ConfirmDialog
      isOpen={isOpen}
      tone="neutral"
      title={intl.formatMessage(coverEditorMessages.useTitle, { song: songTitle })}
      description={intl.formatMessage(coverEditorMessages.useText)}
      confirmLabel={intl.formatMessage(coverEditorMessages.useConfirm)}
      isBusy={isBusy}
      error={error === null ? undefined : intl.formatMessage(useAsCoverErrorMessages[error])}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <div className={styles.swap}>
        <figure>
          {currentCoverUrl === null ? <span className={styles.swapEmpty} /> : <img src={currentCoverUrl} alt="" />}
          <figcaption>{intl.formatMessage(coverEditorMessages.useNow)}</figcaption>
        </figure>
        <span className={styles.swapArrow} aria-hidden>
          →
        </span>
        <figure>
          <CoverThumb doc={doc} size={SWAP_PX} watermark={watermark} />
          <figcaption>{intl.formatMessage(coverEditorMessages.useNew)}</figcaption>
        </figure>
      </div>
      {assetCount > 0 && (
        <p className={styles.hint}>{intl.formatMessage(coverEditorMessages.useAssets, { count: assetCount })}</p>
      )}
      {watermark && (
        <p className={styles.hint}>{withBrandWord(intl.formatMessage(coverEditorMessages.useWatermark))}</p>
      )}
    </ConfirmDialog>
  );
};

export const UseAsCoverDialog = TypedMemo(UseAsCoverDialogComponent);
